import { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import { useAuth } from '../contexts/AuthContext';
import API from '../lib/api';

// Route permission mapping for admins and staff
const ROUTE_PERMS = {
  '/app/dashboard': 'dashboard',
  '/app/credits': 'credits',
  '/app/staff': 'create_users',
  '/app/reports': 'reports',
  '/app/broadcast': 'broadcast',
  '/app/channel-broadcast': 'broadcast',
  '/app/scheduled-broadcast': 'broadcast',
  '/app/broadcast-reports': 'reports',
  '/app/user-reports': 'reports',
  '/app/mobile-reports': 'reports',
  '/app/bot-live-chat': 'live_chat',
  '/app/whatsapp-chat': 'whatsapp_chat',
  '/app/bot-settings': 'bot_settings',
  '/app/bot-automation': 'bot_automation',
  '/app/bot-cloud-ai': 'bot_automation',
  '/app/cloud-ai': 'bot_automation',
  '/app/edit-bot': 'edit_bots',
  '/app/auto-messages': 'auto_messages',
};

function getFirstPage(user) {
  if (user?.role === 'super_admin') return '/app/dashboard';
  const perms = user?.permissions || [];
  if (user?.role === 'admin' && perms.includes('dashboard')) return '/app/dashboard';
  if (perms.includes('assigned_chats')) return '/app/bot-live-chat?tab=assigned';
  if (perms.includes('live_chat')) return '/app/bot-live-chat';
  if (perms.includes('whatsapp_chat')) return '/app/whatsapp-chat';
  if (perms.includes('admin_chat')) return '/app/chat';
  if (perms.includes('bot_automation')) return '/app/bot-automation';
  if (perms.includes('reports')) return '/app/reports';
  if (perms.includes('broadcast')) return '/app/broadcast';
  if (perms.includes('auto_messages')) return '/app/auto-messages';
  if (perms.includes('credits')) return '/app/credits';
  if (perms.includes('create_users')) return '/app/staff';
  return '/app/chat';
}

export default function Layout() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [bots, setBots] = useState([]);
  const [selectedBot, setSelectedBot] = useState(null);

  useEffect(() => {
    API.get('/bots').then(r => {
      const loaded = r.data.bots || [];
      setBots(loaded);
      if (user?.role === 'admin' && loaded.length === 1 && !selectedBot) {
        setSelectedBot(loaded[0].id);
      }
    }).catch(() => {});
  }, [user?.role]);

  // Route protection: redirect if admin/staff doesn't have permission for current page
  useEffect(() => {
    if (!user || user.role === 'super_admin') return;
    const path = location.pathname;
    const perms = user.permissions || [];

    // Live chat and assigned chats tab check
    if (path === '/app/bot-live-chat') {
      const isAssignedTab = location.search.includes('tab=assigned');
      if (isAssignedTab && (perms.includes('assigned_chats') || perms.includes('live_chat'))) return;
      if (!isAssignedTab && perms.includes('live_chat')) return;
      navigate(getFirstPage(user), { replace: true });
      return;
    }

    // Support chat check for staff
    if (path === '/app/chat') {
      if (user.role === 'staff' && !perms.includes('admin_chat')) {
        navigate(getFirstPage(user), { replace: true });
      }
      return;
    }

    const requiredPerm = ROUTE_PERMS[path];
    if (requiredPerm && !perms.includes(requiredPerm)) {
      navigate(getFirstPage(user), { replace: true });
    }
  }, [location.pathname, location.search, user, navigate]);

  const isChatRoute = ['/app/bot-live-chat', '/app/chat', '/app/whatsapp-chat', '/app/bot-automation'].some(p => location.pathname.startsWith(p));
  const isAutomationRoute = location.pathname.startsWith('/app/bot-automation');

  return (
    <div className={`flex ${isChatRoute ? 'h-[100dvh] max-h-[100dvh] overflow-hidden' : 'min-h-screen'}`} data-testid="app-layout">
      <Sidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        bots={bots}
        selectedBot={selectedBot}
        onSelectBot={setSelectedBot}
      />
      <main className={`flex-1 ${isChatRoute ? 'h-[100dvh] max-h-[100dvh] overflow-hidden flex flex-col' : 'min-h-screen overflow-auto'}`}>
        <div className={isAutomationRoute ? 'p-0 m-0 w-full flex-1 flex flex-col min-h-0 overflow-hidden h-full' : (isChatRoute ? 'p-0 md:p-3 lg:p-4 max-w-[1600px] w-full flex-1 flex flex-col min-h-0 overflow-hidden h-full' : 'p-3 sm:p-4 md:p-6 lg:p-8 max-w-[1400px] overflow-x-hidden')}>
          <Outlet context={{ bots, selectedBot, setSelectedBot, setBots }} />
        </div>
      </main>
    </div>
  );
}
